import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import kotlinx.coroutines.launch

@Composable
fun RosterScreen(
    token: String?,
    role: String?,
    teamId: String,
    teamName: String,
    seasonId: String,
    onBack: () -> Unit,
) {
    var roster by remember { mutableStateOf<List<RosterEntryDto>>(emptyList()) }
    var pickablePlayers by remember { mutableStateOf<List<PlayerDto>>(emptyList()) }
    var seasonActive by remember { mutableStateOf(true) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var refreshKey by remember { mutableStateOf(0) }

    var selectedExistingPlayerId by remember { mutableStateOf("") }
    var newPlayerName by remember { mutableStateOf("") }
    var newPlayerFullName by remember { mutableStateOf("") }
    var newPlayerGender by remember { mutableStateOf("male") }
    var newPlayerRegistered by remember { mutableStateOf(false) }
    var newPlayerCard by remember { mutableStateOf(false) }
    var newPlayerEmail by remember { mutableStateOf("") }
    var newPlayerPhone by remember { mutableStateOf("") }

    var editingPlayerId by remember { mutableStateOf<String?>(null) }
    var editName by remember { mutableStateOf("") }
    var editFullName by remember { mutableStateOf("") }
    var editGender by remember { mutableStateOf("male") }
    var editRegistered by remember { mutableStateOf(false) }
    var editCard by remember { mutableStateOf(false) }
    var editEmail by remember { mutableStateOf("") }
    var editPhone by remember { mutableStateOf("") }

    val scope = rememberCoroutineScope()

    LaunchedEffect(refreshKey) {
        loading = true
        try {
            roster = Api.fetchRoster(token, teamId, seasonId)
            seasonActive = Api.fetchSeason(token, seasonId).status == "active"
            val allPlayers = Api.fetchAllPlayers(token)
            val rosteredInSeason = Api.fetchRosteredPlayerIdsForSeason(token, seasonId)
            pickablePlayers = allPlayers.filter { it.id !in rosteredInSeason }
            error = null
        } catch (e: Throwable) {
            error = "Načtení soupisky selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    Div(attrs = { classes("card") }) {
        H2 { Text("Soupiska — $teamName") }
        Button(attrs = { onClick { onBack() } }) { Text("Zpět") }
        if (loading) Text("Načítám…")
        error?.let { Div { Text("Chyba: $it") } }

        Div(attrs = { classes("table-scroll") }) {
        Table {
            Tr {
                Th { Text("Zobrazované jméno") }
                Th { Text("Celé jméno") }
                Th { Text("Pohlaví") }
                Th { Text("Registrovaný") }
                Th { Text("Kartička") }
                Th { Text("Email") }
                Th { Text("Telefon") }
                Th { Text("Kapitán") }
                Th { }
            }
            roster.forEach { entry ->
                if (editingPlayerId == entry.playerId) {
                    Tr {
                        Td {
                            Input(type = InputType.Text) {
                                classes("roster-edit-input")
                                value(editName)
                                onInput { editName = it.value }
                            }
                        }
                        Td {
                            Input(type = InputType.Text) {
                                classes("roster-edit-input")
                                value(editFullName)
                                onInput { editFullName = it.value }
                            }
                        }
                        Td {
                            Select(attrs = { onChange { editGender = it.value ?: "male" } }) {
                                Option("male", attrs = { if (editGender == "male") selected() }) { Text("Muž") }
                                Option("female", attrs = { if (editGender == "female") selected() }) { Text("Žena") }
                            }
                        }
                        Td {
                            CheckboxInput(checked = editRegistered) { onInput { editRegistered = it.value } }
                        }
                        Td {
                            CheckboxInput(checked = editCard) { onInput { editCard = it.value } }
                        }
                        Td {
                            Input(type = InputType.Text) {
                                classes("roster-edit-input")
                                value(editEmail)
                                onInput { editEmail = it.value }
                            }
                        }
                        Td {
                            Input(type = InputType.Text) {
                                classes("roster-edit-input")
                                value(editPhone)
                                onInput { editPhone = it.value }
                            }
                        }
                        Td { Text(if (entry.captain) "Ano" else "—") }
                        Td {
                            Button(attrs = {
                                onClick {
                                    if (editName.isNotBlank()) {
                                        scope.launch {
                                            try {
                                                Api.updatePlayer(
                                                    token, entry.playerId, editName,
                                                    editFullName.ifBlank { null },
                                                    editGender, editRegistered, editCard,
                                                    editEmail.ifBlank { null },
                                                    editPhone.ifBlank { null },
                                                )
                                                editingPlayerId = null
                                                refreshKey++
                                            } catch (e: Throwable) {
                                                error = "Uložení selhalo: ${e.apiMessage()}"
                                            }
                                        }
                                    }
                                }
                            }) { Text("Uložit") }
                            Button(attrs = { onClick { editingPlayerId = null } }) { Text("Zrušit") }
                        }
                    }
                } else {
                    Tr {
                        Td { Text(entry.player?.name ?: entry.playerId) }
                        Td { Text(entry.player?.fullName ?: "—") }
                        Td { Text(genderLabel(entry.player?.gender)) }
                        Td { Text(if (entry.player?.registered == true) "Ano" else "Ne") }
                        Td { Text(if (entry.player?.card == true) "Ano" else "Ne") }
                        Td { Text(entry.player?.email ?: "—") }
                        Td { Text(entry.player?.phone ?: "—") }
                        Td {
                            if (role.canManage()) {
                                CheckboxInput(checked = entry.captain) {
                                    onInput { ev ->
                                        val wantCaptain = ev.value
                                        // Kapitán musí mít vyplněný email + telefon (kontroluje i DB).
                                        if (wantCaptain &&
                                            (entry.player?.email.isNullOrBlank() || entry.player?.phone.isNullOrBlank())
                                        ) {
                                            error = "Kapitán musí mít vyplněný email a telefon (doplň je v evidenci hráče)."
                                        } else {
                                            scope.launch {
                                                try {
                                                    Api.setRosterCaptain(token, entry.id, wantCaptain)
                                                    error = null
                                                    refreshKey++
                                                } catch (e: Throwable) {
                                                    error = "Nastavení kapitána selhalo: ${e.apiMessage()}"
                                                }
                                            }
                                        }
                                    }
                                }
                            } else {
                                Text(if (entry.captain) "Ano" else "—")
                            }
                        }
                        Td {
                            if (role.canManage()) {
                                Button(attrs = {
                                    onClick {
                                        editingPlayerId = entry.playerId
                                        editName = entry.player?.name ?: ""
                                        editFullName = entry.player?.fullName ?: ""
                                        editGender = entry.player?.gender ?: "male"
                                        editRegistered = entry.player?.registered ?: false
                                        editCard = entry.player?.card ?: false
                                        editEmail = entry.player?.email ?: ""
                                        editPhone = entry.player?.phone ?: ""
                                    }
                                }) { Text("Upravit") }
                                if (!seasonActive) {
                                    Button(attrs = {
                                        onClick {
                                            scope.launch {
                                                try {
                                                    Api.removeFromRoster(token, entry.id)
                                                    refreshKey++
                                                } catch (e: Throwable) {
                                                    error = "Odebrání selhalo: ${e.apiMessage()}"
                                                }
                                            }
                                        }
                                    }) { Text("Odebrat") }
                                }
                            }
                        }
                    }
                }
            }
        }
        }
        Div { Text("${roster.size} / 6 hráčů") }
        if (seasonActive) {
            Div { Text("Odebrání ze soupisky je možné jen mimo aktivní sezónu.") }
        }

        if (role.canManage() && roster.size >= 6) {
            Div { Text("Soupiska je plná.") }
        } else if (role.canManage()) {
            H3 { Text("Přidat existujícího hráče (bez týmu v této sezóně)") }
            Div {
                Select(attrs = { onChange { selectedExistingPlayerId = it.value ?: "" } }) {
                    Option("") { Text("— vyber hráče —") }
                    pickablePlayers.forEach { player ->
                        Option(player.id) { Text("${player.name} (${genderLabel(player.gender)})") }
                    }
                }
                Button(attrs = {
                    onClick {
                        if (selectedExistingPlayerId.isNotBlank()) {
                            scope.launch {
                                try {
                                    Api.addToRoster(token, selectedExistingPlayerId, teamId, seasonId)
                                    selectedExistingPlayerId = ""
                                    refreshKey++
                                } catch (e: Throwable) {
                                    error = "Přidání selhalo: ${e.apiMessage()}"
                                }
                            }
                        }
                    }
                }) { Text("Přidat") }
            }

            H3 { Text("Založit nového hráče") }
            Div {
                Input(type = InputType.Text) {
                    placeholder("Zobrazované jméno")
                    value(newPlayerName)
                    onInput { newPlayerName = it.value }
                }
                Input(type = InputType.Text) {
                    placeholder("Celé jméno (nepovinné)")
                    value(newPlayerFullName)
                    onInput { newPlayerFullName = it.value }
                }
                Select(attrs = { onChange { newPlayerGender = it.value ?: "male" } }) {
                    Option("male") { Text("Muž") }
                    Option("female") { Text("Žena") }
                }
                Label { CheckboxInput(checked = newPlayerRegistered) { onInput { newPlayerRegistered = it.value } }; Text(" Registrovaný") }
                Label { CheckboxInput(checked = newPlayerCard) { onInput { newPlayerCard = it.value } }; Text(" Kartička") }
                Input(type = InputType.Text) {
                    placeholder("Email (nepovinné)")
                    value(newPlayerEmail)
                    onInput { newPlayerEmail = it.value }
                }
                Input(type = InputType.Text) {
                    placeholder("Telefon (nepovinné)")
                    value(newPlayerPhone)
                    onInput { newPlayerPhone = it.value }
                }
                Button(attrs = {
                    onClick {
                        if (newPlayerName.isNotBlank()) {
                            scope.launch {
                                try {
                                    val player = Api.createPlayer(
                                        token,
                                        newPlayerName,
                                        fullName = newPlayerFullName.ifBlank { null },
                                        gender = newPlayerGender,
                                        registered = newPlayerRegistered,
                                        card = newPlayerCard,
                                        email = newPlayerEmail.ifBlank { null },
                                        phone = newPlayerPhone.ifBlank { null },
                                    )
                                    Api.addToRoster(token, player.id, teamId, seasonId)
                                    newPlayerName = ""
                                    newPlayerFullName = ""
                                    newPlayerRegistered = false
                                    newPlayerCard = false
                                    newPlayerEmail = ""
                                    newPlayerPhone = ""
                                    refreshKey++
                                } catch (e: Throwable) {
                                    error = "Založení hráče selhalo: ${e.apiMessage()}"
                                }
                            }
                        }
                    }
                }) { Text("Založit a přidat") }
            }
        }
    }
}
